import React, { Component } from "react";
import moment from 'moment';
import {
    VictoryChart,
    VictoryAxis,
    VictoryBrushContainer,
    VictoryLine,
    VictoryTooltip,
    VictoryTheme,
    VictoryLabel,
    createContainer
} from "victory";



const VictoryZoomVoronoiContainer = createContainer("zoom", "voronoi");

// Lowest observed or forecast reading, minus a fixed offset, used as the
// y-axis floor. Clamped to a small positive number because this chart's
// y-axis is log-scaled, where a domain minimum of <= 0 is undefined.
const Y_AXIS_OFFSET = 5;
const LOG_AXIS_FLOOR = 0.1;

function flowAxisMinimum(readings) {
    const lowest = readings.reduce((min, p) => {
        // Number(null) and Number('') are 0, not NaN, so missing readings
        // have to be rejected before the numeric conversion.
        if (!p || p.value === null || p.value === undefined || p.value === '') {
            return min;
        }

        const value = Number(p.value);
        return isFinite(value) ? Math.min(min, value) : min;
    }, Infinity);

    if (lowest === Infinity) {
        return LOG_AXIS_FLOOR;
    }

    return Math.max(lowest - Y_AXIS_OFFSET, LOG_AXIS_FLOOR);
}

class LargeFlowChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            observedData: [],
            forecastData: [],
            totalData: [],
            tickValues: [],
            chartMax: null,
            yMin: LOG_AXIS_FLOOR
        };
    }

    handleZoom(domain) {
        this.setState({ selectedDomain: domain });
    }

    handleBrush(domain) {
        this.setState({ zoomDomain: domain });
    }


    componentDidMount() {

        // create formatted date object for observed
        const observed = this.props.data.map(p => {
            p.Date = moment(p.dateTime).format();
            p.Date = new Date(p.Date);
            return p;
        });

        // create formatted date object for observed
        const forecast = this.props.forecastData.map(p => {
            p.Date = moment(p.dateTime).format();
            p.Date = new Date(p.Date);
            return p;
        });

        // total readings
        const total = observed.concat(forecast);

        this.setState({ observedData: observed });
        this.setState({ forecastData: forecast });
        this.setState({ totalData: total });

        // y-axis floor: 5 below the lowest reading across both series
        this.setState({ yMin: flowAxisMinimum(total) });


        // set tick marks to occur daily from day of
        // first observed reading to last forecast reading
        const startDay = moment(total[0].Date);
        const endDay = moment(total[total.length - 1].Date);
        const timeline = endDay.diff(startDay, 'days');

        this.setState({ startDay, endDay });

        const newTickValues = []

        for (let i = 0; i < timeline; i++) {
            var newTickDay = moment(startDay).add(1, 'days')
            newTickValues.push(moment(newTickDay).format('MMM D'))

        }

        this.setState({ tickValues: newTickValues });

        this.setState({ chartMax: 500 });

        
    }

    // return forecast or observed label for chart hover/tooltip
    renderVeronoiLabels = (datum) => {
        let dataObj = this.state.totalData.filter(reading => reading.Date === datum.Date);
        dataObj = dataObj[0];
        let label = '';
        if (moment(datum._x).isBefore() && dataObj) {
            label = `Observed Flow: ${dataObj.value} cfs \n ${moment(datum._x).format('lll')}`;
        } else if (!moment(datum._x).isBefore() && dataObj) {
            label = `Forecast Flow: ${dataObj.value} cfs \n ${moment(datum._x).format('lll')}`;
        }
        return label;
    }

    render() {
        return (
            <div class="chartWrapper">
                <VictoryChart
                    minDomain={{ y: this.state.yMin }}
                    padding={{ top: 15, left: 85, right: 50, bottom: 10 }}
                    width={700}
                    height={60}
                    scale={{ y: "log", x: "time" }}
                    containerComponent={
                        <VictoryBrushContainer
                            brushDimension="x"
                            brushDomain={this.state.selectedDomain}
                            onBrushDomainChange={this.handleBrush.bind(this)}
                            brushStyle={{ stroke: "transparent", fill: "#369de0", fillOpacity: 0.2 }}
                        />
                    }
                >
                    <VictoryAxis dependentAxis
                        scale="log"
                        orientation="left"
                        standalone={false}
                        tickCount={1}
                        // tickValues={[0, this.state.chartMax]}
                        style={{
                            axis: { stroke: "#756f6a" },
                            axisLabel: { fontSize: 20, padding: 30 },
                            ticks: { stroke: "grey", size: 1 },
                            tickLabels: { fontSize: 0, padding: 5 }
                        }}
                    />

                    <VictoryLine
                        style={{
                            data: { stroke: "gray" }
                        }}
                        data={this.state.totalData}
                        x="Date"
                        y={"value"}
                    />
                </VictoryChart>
                <p className="dragMessage">Click and drag the edges or position of blue brush container to zoom to or select data ranges</p>

                <VictoryChart
                    padding={{ top: 10, left: 85, right: 50, bottom: 60 }}
                    minDomain={{ y: this.state.yMin }}
                    width={700}
                    height={350}
                    scale={{ y: "log", x: "time" }}
                    theme={VictoryTheme.material}
                    containerComponent={
                        <VictoryZoomVoronoiContainer
                            zoomDimension="x"
                            zoomDomain={this.state.zoomDomain}
                            onZoomDomainChange={this.handleZoom.bind(this)}
                            labelComponent={<VictoryTooltip constrainToVisibleArea dy={50} pointerLength={0} cornerRadius={0} flyoutStyle={{ fill: "white" }} />}
                            labels={({ datum }) => this.renderVeronoiLabels(datum)}
                        />
                    }
                >
                    <VictoryAxis dependentAxis
                        scale={{ y: "log" }}
                        orientation="left"
                        standalone={false}
                        tickFormat={(t) => `${Math.round(t)} cfs`}
                        label="Discharge (cfs)"
                        axisLabelComponent={<VictoryLabel dy={-60} />}

                    />
                    <VictoryAxis
                        scale="time"
                        label="Site Date/Time (EST)"
                        axisLabelComponent={<VictoryLabel dy={35} />}
                    // tickValues={this.setState.tickValues}
                    // tickFormat={x => moment(x).format('MMM D')}
                    />
                    <VictoryLine
                        style={{
                            data: { stroke: "#1f89c7", strokeWidth: 1 }
                        }}
                        data={this.state.observedData}
                        x="Date"
                        y={"value"}
                    />
                    <VictoryLine
                        style={{
                            data: { stroke: "#9a9696", strokeWidth: 1 }
                }}
                        data={this.state.forecastData}
                        x="Date"
                        y={"value"}
                    />


                </VictoryChart>
            </div>
        );
    }
}

export default LargeFlowChart;


// WEBPACK FOOTER //
// ./src/components/LargeFlowChart/index.js